import { zodResolver } from '@hookform/resolvers/zod';
import * as customerPortalApi from '@kk/api/modules/customerPortal';
import { isEmpty } from '@kk/shared/utils/objects';
import Button from '@kk/ui/components/Button';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { FormProvider, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';
import * as Fields from './CalculatorFields';
import { FieldNames } from './CalculatorFields';

type FormData = {
  [FieldNames.PrincipalAmount]: number;
  [FieldNames.MaturityInYears]: number;
  [FieldNames.LoanProduct]: customerPortalApi.ContractLoanTypeEnum;
  [FieldNames.AmortizationProfile]: customerPortalApi.AmortizationProfile;
  [FieldNames.FixedInterestPeriod]: customerPortalApi.FixedInterestPeriodType;
  [FieldNames.FixedInterestYears]: number | null;
  [FieldNames.InterestPaymentFrequency]: customerPortalApi.PaymentFreqEnum;
};

export type CalculatorFormMethods = Pick<
  UseFormReturn<FormData>,
  'formState' | 'reset' | 'getValues' | 'setValue'
>;

type CalculatorFormProps = {
  className?: string;
  formClassName?: string;
  onSuccess: (data: customerPortalApi.CalculationInfoResponse) => void;
  onSubmit: (
    data: customerPortalApi.StandaloneRequest,
  ) => Promise<customerPortalApi.CalculationInfoResponse>;
  onFormStateChange?: (hasDirtyFields: boolean) => void;
};

function isFixedInterestPeriodType(
  value: string,
): value is customerPortalApi.FixedInterestPeriodType {
  return value in customerPortalApi.FixedInterestPeriodType;
}

export const CalculatorForm = forwardRef<
  CalculatorFormMethods,
  CalculatorFormProps
>(function CalculatorForm(
  { className, formClassName, onSuccess, onSubmit, onFormStateChange },
  ref,
) {
  const { t } = useTranslation(['ui']);
  const [isPending, setIsPending] = useState(false);

  const schema = useMemo(() => {
    return z.object({
      [FieldNames.PrincipalAmount]: z.coerce
        .number({ message: t('form.input.required') })
        .min(1, { message: t('form.input.required') }),
      [FieldNames.MaturityInYears]: z.coerce
        .number({ message: t('form.input.required') })
        .min(1, { message: t('form.input.required') }),
      [FieldNames.LoanProduct]: z.nativeEnum(
        customerPortalApi.ContractLoanTypeEnum,
        {
          message: t('form.input.required'),
        },
      ),
      [FieldNames.AmortizationProfile]: z.nativeEnum(
        customerPortalApi.AmortizationProfile,
        {
          message: t('form.input.required'),
        },
      ),
      [FieldNames.FixedInterestPeriod]: z.coerce
        .string()
        .transform((value, ctx) => {
          const parsed = Number(value)
            ? customerPortalApi.FixedInterestPeriodType.Years
            : value;
          if (isFixedInterestPeriodType(parsed) === false) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('form.input.required'),
            });
            return z.NEVER;
          }
          return parsed;
        }),
      [FieldNames.FixedInterestYears]: z.coerce
        .number({ message: t('form.input.required') })
        .max(30, { message: t('form.input.required') })
        .optional()
        .transform((value) => {
          if (value === 0 || value === undefined) return null;
          return value;
        }),
      [FieldNames.InterestPaymentFrequency]: z.nativeEnum(
        customerPortalApi.PaymentFreqEnum,
        {
          message: t('form.input.required'),
        },
      ),
    });
  }, [t]);

  const methods = useForm<z.infer<typeof schema>>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
  });

  // Expose specific form methods to parent via imperative handle
  useImperativeHandle(
    ref,
    () => ({
      formState: methods.formState,
      reset: methods.reset,
      getValues: methods.getValues,
      setValue: methods.setValue,
    }),
    [methods],
  );

  const noFieldsAreDirty = isEmpty(methods.formState.dirtyFields);
  const hasDirtyFields = noFieldsAreDirty === false;

  // Notify parent about form state changes
  useEffect(() => {
    onFormStateChange?.(hasDirtyFields);
  }, [hasDirtyFields, onFormStateChange]);

  const handleSubmit = async (data: customerPortalApi.StandaloneRequest) => {
    setIsPending(true);
    try {
      const result = await onSubmit(data);
      onSuccess(result);
      methods.reset(undefined, {
        keepValues: true,
        keepDirty: false,
        keepIsValid: true,
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <section className={twMerge('@container h-full', className)}>
      <FormProvider {...methods}>
        <form
          className={twMerge(
            '@xl:h-full lg:border rounded-md border-neutral-10 lg:shadow-sm-dl/8 grid @xl:grid-cols-2 items-start p-8 gap-8',
            formClassName,
          )}
          onSubmit={methods.handleSubmit(handleSubmit)}
        >
          <div className="flex flex-col gap-8">
            <Fields.PrincipalAmount />
            <Fields.MaturityInYears />
            <Fields.ProductType />
          </div>
          <div className="flex flex-col gap-8">
            <Fields.AmortizationProfile />
            <Fields.FixedInterestPeriod />
            <Fields.RateFrequency />
          </div>
          <footer className="flex md:justify-end col-span-full h-full">
            <Button
              theme="primary"
              type="submit"
              className="w-full flex @xl:w-auto items-center justify-center @xl:self-end"
              disabled={
                isPending || !methods.formState.isValid || noFieldsAreDirty
              }
              loading={isPending}
            >
              {t('calculatorForm.submit')}
            </Button>
          </footer>
        </form>
      </FormProvider>
    </section>
  );
});

export default CalculatorForm;
